import {
  appleClientId,
  facebookAppId,
  instagramAppId,
  lineClientId,
  oauthUniversalLinkPath,
  oauthUniversalLinkRoot,
  tiktokClientKey,
  twitterClientId,
} from "@constants/Env";
import { clickAction } from "@lib/util/analytics";
import AsyncStorage from "@react-native-async-storage/async-storage";
import days from "dayjs";
import * as Crypto from "expo-crypto";
import _ from "lodash";
import qs from "query-string";
import { Platform } from "react-native";
import { OAuthRedirectWeb } from "../../types";

const CHARSET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
// Meta Graph API のバージョン。バックエンド (config/settings.yml の facebook_graph_api.version) と揃える。
// 各バージョンは公開から約2年で提供終了になるため、Meta からの移行通知に合わせて更新する。
// https://developers.facebook.com/docs/graph-api/changelog/
const FACEBOOK_GRAPH_API_VERSION = "v26.0";
const codeChallengeMethod = "S256";
const responseType = "code";
const authorizationName = "AUTHORIZATION_NAME";
type LoginMedia = "Line" | "Apple";
type LinkingMedia = "Instagram" | "Twitter" | "Tiktok";

// バックエンドのInstagramAuthProviderと値を揃える。
export type InstagramAuthProvider = "FACEBOOK_LOGIN" | "INSTAGRAM_LOGIN";

type requestConfig = {
  scopes: string[];
  // TikTokのスコープはカンマ区切り。省略時はスペース区切り。
  scopeSeparator?: string;
  usePKCE: boolean;
  // TikTokのcode_challengeはSHA256の16進数文字列。
  codeChallengeEncoding?: "base64url" | "hex";
  clientId: string;
  // TikTokのクライアント識別子はclient_keyで送る。
  clientIdParamName?: string;
  prompt?: string;
  extraParams?: {
    [index: string]: string;
  };
  discovery: {
    authorizationEndpoint: string;
  };
  // Instagram連携時にlinkSnsへ渡す認証方式。
  authProvider?: InstagramAuthProvider;
};

export type MakeConfigOptions = {
  // 省略時はFacebook Loginを使う。
  instagramAuthProvider?: InstagramAuthProvider;
};

type requestParams = requestConfig & {
  redirectUri: string;
  callbackUrl?: string;
  platform: SupportType;
  action: ActionType;
};

export type SupportType = LoginMedia | LinkingMedia;
export type ActionType = "Login" | "Linking";

export function isLoginMedia(media: SupportType): media is LoginMedia {
  return media === "Line" || media === "Apple";
}

export type SignUp = {
  id: string;
  commonName: string | null;
  email: string | null;
};

export type Login = {
  token: string;
  refreshToken: string;
};

export function isLogin(data: SignUp | Login): data is Login {
  return "token" in data;
}

export type Authorization = {
  codeVerifier: string;
  csrfState: string;
  callbackUrl?: string;
  platform: SupportType;
  action: ActionType;
  expired: number;
  authProvider?: InstagramAuthProvider;
};

export function sendLog(type: SupportType) {
  switch (type) {
    case "Apple":
      clickAction("LinkingApple");
      break;
    case "Line":
      clickAction("LinkingLine");
      break;
    case "Instagram":
      clickAction("LinkingInstagram");
      break;
    case "Twitter":
      clickAction("LinkingTwitter");
      break;
    case "Tiktok":
      clickAction("LinkingTiktok");
      break;
    default:
  }
}

export function makeRedirectUri(): string {
  if (Platform.OS === "web") {
    return `${window.location.origin}/${OAuthRedirectWeb}`;
  }
  return `${oauthUniversalLinkRoot}/${oauthUniversalLinkPath}`;
}

function makeInstagramConfig(
  authProvider: InstagramAuthProvider
): requestConfig {
  if (authProvider === "INSTAGRAM_LOGIN") {
    // https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login/business-login
    return {
      scopes: [
        "instagram_business_basic",
        "instagram_business_manage_insights",
      ],
      // Instagram Loginのscopeはカンマ区切り。
      scopeSeparator: ",",
      clientId: instagramAppId,
      // PKCEには対応していないため、client_secretでトークンを交換する。
      usePKCE: false,
      discovery: {
        authorizationEndpoint: "https://www.instagram.com/oauth/authorize",
      },
      extraParams: {
        // 「Facebookでログイン」を非表示にする。
        enable_fb_login: "0",
      },
      authProvider: "INSTAGRAM_LOGIN",
    };
  }

  // Facebookページに紐づくInstagramプロアカウントを連携する。
  return {
    scopes: [
      "pages_read_engagement",
      "instagram_basic",
      "pages_show_list",
      "instagram_manage_insights",
      "public_profile",
      "business_management",
    ],
    clientId: facebookAppId,
    usePKCE: true,
    discovery: {
      authorizationEndpoint: `https://www.facebook.com/${FACEBOOK_GRAPH_API_VERSION}/dialog/oauth`,
    },
    authProvider: "FACEBOOK_LOGIN",
  };
}

export function makeConfig(
  type: SupportType,
  options: MakeConfigOptions = {}
): requestConfig {
  switch (type) {
    case "Instagram":
      return makeInstagramConfig(
        options.instagramAuthProvider ?? "FACEBOOK_LOGIN"
      );
    case "Twitter":
      return {
        scopes: ["tweet.read", "users.read", "offline.access"],
        clientId: twitterClientId,
        usePKCE: true,
        discovery: {
          authorizationEndpoint: "https://twitter.com/i/oauth2/authorize",
        },
      };
    case "Tiktok":
      return {
        // usernameの取得にはuser.info.profile、フォロワー数にはuser.info.statsが必要。
        scopes: ["user.info.basic", "user.info.profile", "user.info.stats"],
        scopeSeparator: ",",
        clientId: tiktokClientKey,
        clientIdParamName: "client_key",
        // TikTokのPKCE仕様:
        // https://developers.tiktok.com/doc/login-kit-desktop
        usePKCE: true,
        codeChallengeEncoding: "hex",
        discovery: {
          authorizationEndpoint: "https://www.tiktok.com/v2/auth/authorize/",
        },
      };
    case "Apple":
      return {
        scopes: [],
        clientId: appleClientId,
        // AppleはPKCE対応していない
        usePKCE: false,
        discovery: {
          authorizationEndpoint: "https://appleid.apple.com/auth/authorize",
        },
      };
    case "Line":
    default:
      return {
        scopes: ["profile", "openid", "email"],
        clientId: lineClientId,
        usePKCE: true,
        prompt: "content",
        discovery: {
          authorizationEndpoint: "https://access.line.me/oauth2/v2.1/authorize",
        },
        extraParams: {
          bot_prompt: "aggressive",
        },
      };
  }
}

export async function makeAuthorizationEndpoint(
  request: requestParams
): Promise<string> {
  const params: Record<string, string> = {};
  const csrfState = generateRandom(8);
  const { codeVerifier, codeChallenge } = request.usePKCE
    ? await buildCodeAsync(128, request.codeChallengeEncoding)
    : { codeVerifier: "", codeChallenge: "" };

  params.redirect_uri = request.redirectUri;
  params.state = csrfState;
  params[request.clientIdParamName ?? "client_id"] = request.clientId;
  params.response_type = responseType;
  if (request.scopes.length > 0) {
    params.scope = request.scopes.join(request.scopeSeparator ?? " ");
  }

  if (request.extraParams !== undefined) {
    Object.keys(request.extraParams).forEach((key) => {
      if (request.extraParams !== undefined) {
        params[key] = request.extraParams[key];
      }
    });
  }

  if (request.prompt !== undefined) {
    params.prompt = request.prompt;
  }

  if (request.usePKCE) {
    params.code_challenge_method = codeChallengeMethod;
    params.code_challenge = codeChallenge;
  }
  const query = qs.stringify(params);
  const authorization: Authorization = {
    codeVerifier,
    csrfState,
    callbackUrl: request.callbackUrl,
    platform: request.platform,
    action: request.action,
    expired: days().add(10, "minutes").unix(),
    // コードの交換にも同じ認証方式を使うため、リダイレクト前に保存する。
    authProvider: request.authProvider,
  };
  await AsyncStorage.setItem(authorizationName, JSON.stringify(authorization));
  return `${request.discovery.authorizationEndpoint}?${query}`;
}

export async function getAuthorizationInfo(): Promise<Authorization | null> {
  try {
    const data = await AsyncStorage.getItem(authorizationName);
    if (data === null) {
      throw new Error("連携に失敗しました");
    }
    const {
      codeVerifier,
      csrfState,
      callbackUrl,
      platform,
      action,
      expired,
      authProvider,
    } = JSON.parse(data);

    if (
      _.isNil(csrfState) ||
      _.isNil(platform) ||
      _.isNil(action) ||
      _.isNil(expired)
    ) {
      throw new Error("連携に失敗しました");
    }

    return {
      codeVerifier,
      csrfState,
      callbackUrl,
      platform,
      action,
      expired,
      authProvider,
    };
  } catch (e) {
    return null;
  } finally {
    await removeAuthorizationInfo();
  }
}

export async function removeAuthorizationInfo() {
  await AsyncStorage.removeItem(authorizationName);
}

function getRandomValues(input: Uint8Array): Uint8Array {
  const output = input;
  let inputData = input;
  if (input.byteLength !== input.length) {
    inputData = new Uint8Array(input.buffer);
  }

  const bytes = Crypto.getRandomBytes(input.length);

  for (let i = 0; i < bytes.length; i += 1) {
    inputData[i] = bytes[i];
  }

  return output;
}

function convertBufferToString(buffer: Uint8Array): string {
  const state: string[] = [];
  for (let i = 0; i < buffer.byteLength; i += 1) {
    const index = buffer[i] % CHARSET.length;
    state.push(CHARSET[index]);
  }
  return state.join("");
}

function convertToUrlSafeString(b64: string): string {
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

export function generateRandom(size: number): string {
  const buffer = new Uint8Array(size);
  getRandomValues(buffer);
  return convertBufferToString(buffer);
}

async function deriveChallengeAsync(
  code: string,
  encoding: "base64url" | "hex" = "base64url"
): Promise<string> {
  if (encoding === "hex") {
    return Crypto.digestStringAsync(Crypto.CryptoDigestAlgorithm.SHA256, code, {
      encoding: Crypto.CryptoEncoding.HEX,
    });
  }
  const buffer = await Crypto.digestStringAsync(
    Crypto.CryptoDigestAlgorithm.SHA256,
    code,
    {
      encoding: Crypto.CryptoEncoding.BASE64,
    }
  );
  return convertToUrlSafeString(buffer);
}

export async function buildCodeAsync(
  size: number = 128,
  encoding: "base64url" | "hex" = "base64url"
): Promise<{ codeChallenge: string; codeVerifier: string }> {
  const codeVerifier = generateRandom(size);
  const codeChallenge = await deriveChallengeAsync(codeVerifier, encoding);

  return { codeVerifier, codeChallenge };
}
